import { createResourceEntityAdapter } from 'app-components/flux/core/resourceSliceUtils';
import { scheduledTransactionsActions } from 'app-components/flux/scheduled-transactions';
import {
  type ScheduledTransactionType,
  mkScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { getLogger } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import billPresentmentApi from './billPresentmentAPI';

// forward selectors from file
import {
  getCanBVA,
  getHasBVA,
  getIsPotentialExpense,
  getShowBillTab,
  getShowConnectOption,
} from './billPresentmentSelectors';
import { BILL_PRESENTMENT_REDUCER_KEY } from './billPresentmentTypes';
import type { BillPresentmentSlice, Biller, BillerAccount } from './billPresentmentTypes';

const initialState: BillPresentmentSlice = {
  billers: {
    entities: {},
    ids: [],
  },
  billerAccounts: {
    entities: {},
    ids: [],
  },
  // these all refer to billerAccounts
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
};

const log = getLogger('app-components/flux/bill-presentment/slice');

const billerAdapter = createResourceEntityAdapter<Biller>();
export const {
  selectAll: selectAllBillers,
  selectById: selectBillerById,
  // …others available like selectIds, selectEntities, selectTotal
} = billerAdapter.getSelectors((state: BillPresentmentSlice) => state.billers);

const billerAccountAdapter = createResourceEntityAdapter<BillerAccount>();
export const {
  selectAll: selectAllBillerAccounts,
  selectById: selectBillerAccountById,
  // …others available like selectIds, selectEntities, selectTotal
} = billerAccountAdapter.getSelectors((state: BillPresentmentSlice) => state.billerAccounts); // (rootState: { billPresentment: StateType }) => rootState.billPresentment.billerAccounts)

export const slice = createAppSlice({
  name: BILL_PRESENTMENT_REDUCER_KEY,
  initialState: billerAccountAdapter.getInitialState(initialState) as BillPresentmentSlice,
  reducers: (create) => ({
    // GET BillerAccounts
    fetch: create.asyncThunk(
      async (_: void, thunkApi) => {
        log?.debug('Getting billerAccounts');
        const billerAccounts = await billPresentmentApi.fetch();
        billerAccounts.forEach((acc) => {
          if (acc.billerId) {
            thunkApi.dispatch(slice.actions.getBiller(acc.billerId));
          }
        });
        return billerAccounts;
      },
      {
        pending(state) {
          state.isLoading = true;
        },
        fulfilled(state, action) {
          log?.debug('Getting billerAccounts, response', action.payload);
          billerAccountAdapter.setAll(state.billerAccounts, action.payload);
          state.error = undefined;
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
        settled(state) {
          state.isLoading = false;
          state.loadPending = false;
        },
      },
    ),
    // DELETE/de-enroll BillerAccount
    disconnect: create.asyncThunk(
      async (model: ScheduledTransactionType, thunkApi) => {
        // receives ST model as argument
        log?.debug('Deleting BillerAccount from model:', model);
        try {
          const id = model?.presentmentAccountId;
          if (id) await billPresentmentApi.delete(id);
          if (id || model?.billPresentmentAccountId) {
            thunkApi.dispatch(
              scheduledTransactionsActions.updateScheduledTransaction(
                mkScheduledTransaction({
                  id: model?.id,
                  billPresentmentAccountId: '', // clear old BillGoField
                  presentmentAccountId: '', // clear new fiServ field
                }),
                { undo: { userMessage: 'Bill Disconnected', disableUndoAction: true } },
              ),
            );
          }
        } catch (e) {
          if (slice?.actions?.fetch) {
            thunkApi.dispatch(slice.actions.fetch());
          }
          throw e;
        }
      },
      {
        pending(state, action) {
          state.isLoading = true;
          const model = action.meta.arg;
          if (model?.presentmentAccountId) {
            billerAccountAdapter.removeOne(state.billerAccounts, model.presentmentAccountId);
          }
        },
        fulfilled() {
          log?.debug('Delete billerAccount successful');
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
        settled(state) {
          state.isLoading = false;
          state.loadPending = false;
        },
      },
    ),
    // GET Biller
    getBiller: create.asyncThunk(
      async (id: string) => {
        log?.debug('Getting biller', id);
        try {
          return await billPresentmentApi.getBiller(id);
        } catch (e) {
          log.warn('error retriving biller:', id);
          throw e;
        }
      },
      {
        fulfilled(state, action) {
          log?.debug('Get biller successful', action.payload);
          billerAdapter.addOne(state.billers, action.payload);
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
      },
    ),
  }),
  selectors: {
    getLoadPending: (s) => s.loadPending,
    getIsLoading: (s) => s.isLoading,
    getError: (s) => s.error,
    selectAllBillers,
    selectBillerById,
    selectAllBillerAccounts,
    selectBillerAccountById,
    selectBillerForAccountId: (state, id?: string) => {
      const acc = id && selectBillerAccountById(state, id);
      return acc ? selectBillerById(state, acc.billerId) : null;
    },
  },
});

export const billPresentmentActions = slice.actions;
export const billPresentmentSelectors = {
  ...slice.selectors,
  getCanBVA,
  getHasBVA,
  getIsPotentialExpense,
  getShowConnectOption,
  getShowBillTab,
};
export const billPresentmentReducer = slice.reducer;
export { BILL_PRESENTMENT_REDUCER_KEY };

export default slice;
